import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicEvaluationAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SignaturePad } from '../components/SignaturePad';
import { SignatureNotice } from '../components/SignatureNotice';
import RatingScalePanel from '../components/RatingScalePanel';
import { CheckCircle, Mail, ShieldCheck, AlertCircle, ChevronDown } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { LabeledDate, StagedUpload } from '../components/credential/CredentialControls';

const STEPS = { EMAIL: 'email', OTP: 'otp', REVIEW: 'review', DONE: 'done', EXPIRED: 'expired' };

export default function PublicEvaluationSign() {
  const { token } = useParams();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [scopedToken, setScopedToken] = useState('');
  const [ev, setEv] = useState(null);
  const [signature, setSignature] = useState('');
  const [comments, setComments] = useState('');
  const [errMsg, setErrMsg] = useState('');

  const handleRequestOtp = async (e) => {
    e?.preventDefault();
    if (!email) { toast.error('Enter your email to continue'); return; }
    setBusy(true);
    setErrMsg('');
    try {
      await publicEvaluationAPI.requestOtp(token, email.trim());
      toast.success('A 6-digit passcode was emailed to you.');
      setStep(STEPS.OTP);
    } catch (e2) {
      const detail = e2.response?.data?.detail || 'Unable to send passcode';
      if (e2.response?.status === 410) { setStep(STEPS.EXPIRED); setErrMsg(detail); }
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (!otp || otp.length < 4) { toast.error('Enter the 6-digit passcode from your email'); return; }
    setBusy(true);
    try {
      const res = await publicEvaluationAPI.verifyOtp(token, email.trim(), otp.trim());
      const t = res.data.access_token;
      setScopedToken(t);
      const view = await publicEvaluationAPI.view(token, t);
      setEv(view.data);
      setComments(view.data.sign_phase === 'part2'
        ? (view.data.part2?.employee_comments || '')
        : (view.data.employee_comments || ''));
      setStep(STEPS.REVIEW);
    } catch (e2) {
      const detail = e2.response?.data?.detail || 'Verification failed';
      if (e2.response?.status === 410) { setStep(STEPS.EXPIRED); setErrMsg(detail); }
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  const handleSign = async () => {
    if (!signature) { toast.error('Please draw your signature to continue'); return; }
    setBusy(true);
    try {
      await publicEvaluationAPI.sign(token, scopedToken, { signature, employee_comments: comments });
      setStep(STEPS.DONE);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Sign failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="theme-pin-light min-h-screen bg-slate-50 flex flex-col items-center px-4 py-10" data-testid="public-eval-page">
      <div className="w-full max-w-2xl space-y-6">
        <div className="flex items-center gap-3">
          <img src="/tricorps360-horizontal.png" alt="TriCorps360" className="h-10 w-auto" />
          <div className="border-l border-slate-200 pl-3">
            <h1 className="text-xl font-semibold text-slate-900">{ev?.sign_phase === 'part2' ? '6-Month Review' : 'Performance Evaluation'}</h1>
            <p className="text-sm text-slate-500">Secure employee signing portal</p>
          </div>
        </div>

        {step === STEPS.EMAIL && (
          <Card data-testid="step-email">
            <CardHeader>
              <CardTitle>Verify your identity</CardTitle>
              <CardDescription>Enter the email address where you received this link. We'll send you a 6-digit passcode.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleRequestOtp}>
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input id="email" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="email-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full" data-testid="request-otp-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  {busy ? 'Sending...' : 'Email me a passcode'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {step === STEPS.OTP && (
          <Card data-testid="step-otp">
            <CardHeader>
              <CardTitle>Enter your passcode</CardTitle>
              <CardDescription>We just emailed a 6-digit code to <b>{email}</b>. It expires in 10 minutes.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleVerifyOtp}>
                <div className="space-y-2">
                  <Label htmlFor="otp">6-digit passcode</Label>
                  <Input id="otp" inputMode="numeric" pattern="[0-9]*" autoFocus maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="123456" className="text-center text-2xl tracking-[0.4em] font-mono" data-testid="otp-input" />
                </div>
                <div className="flex flex-col gap-2">
                  <Button type="submit" disabled={busy} className="w-full" data-testid="verify-otp-btn">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    {busy ? 'Verifying...' : 'Verify & Continue'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setStep(STEPS.EMAIL)} className="w-full" data-testid="otp-back-btn">
                    Use a different email
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {step === STEPS.REVIEW && ev && (
          <ReviewAndSign ev={ev} token={token} scopedToken={scopedToken} signature={signature} setSignature={setSignature} comments={comments} setComments={setComments} onSubmit={handleSign} busy={busy} />
        )}

        {step === STEPS.DONE && (
          <Card data-testid="step-done">
            <CardContent className="py-10 text-center space-y-3">
              <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto" />
              <h2 className="text-2xl font-semibold text-slate-900">Thank you for signing</h2>
              <p className="text-slate-600">{ev?.sign_phase === 'part2'
                ? 'Your 6-Month Review has been signed and recorded. You can close this window.'
                : 'Your evaluation has been routed to your supervisor for the next signature. You can close this window.'}</p>
            </CardContent>
          </Card>
        )}

        {step === STEPS.EXPIRED && (
          <Card data-testid="step-expired">
            <CardContent className="py-10 text-center space-y-3">
              <AlertCircle className="w-12 h-12 text-amber-600 mx-auto" />
              <h2 className="text-xl font-semibold text-slate-900">This link is no longer valid</h2>
              <p className="text-slate-600">{errMsg || 'The link may have expired or this evaluation has already been signed.'}</p>
              <p className="text-slate-500 text-sm">Please contact your manager to request a fresh signing link.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function ReviewAndSign({ ev, token, scopedToken, signature, setSignature, comments, setComments, onSubmit, busy }) {
  const snap = ev.employee_snapshot || {};
  const rated = (ev.ratings || []).filter(r => r.rating || r.na);
  const scaleIntro = ev.template?.rating_scale_intro || '';
  const scaleLevels = ev.template?.rating_scale_levels || [];
  return (
    <div className="space-y-4" data-testid="step-review">
      {(scaleIntro || scaleLevels.length > 0) && (
        <RatingScalePanel intro={scaleIntro} levels={scaleLevels} defaultOpen={false} />
      )}
      {ev.credentials && ev.credentials.items && ev.credentials.items.length > 0 && (
        <PublicCredentialUpload token={token} scopedToken={scopedToken}
                                credentials={ev.credentials} optional={!!ev.credentials.optional} />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Review your evaluation</CardTitle>
          <CardDescription>Period: {ev.period_from} – {ev.period_to}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Employee" value={`${snap.first_name || ''} ${snap.last_name || ''}`} />
            <Field label="Position" value={snap.position} />
            <Field label="Site / Account" value={snap.assignment_location} />
            <Field label="Direct Supervisor" value={snap.direct_supervisor_name} />
            <Field label="Hire Date" value={snap.hire_date} />
            <Field label="Overall Score" value={ev.overall_average != null ? `${ev.overall_average.toFixed(2)}${ev.overall_band ? ` (${ev.overall_band})` : ''}` : '—'} />
          </div>
        </CardContent>
      </Card>

      {(ev.strengths || ev.areas_for_development || ev.goals || ev.training_assigned || ev.supervisor_comments) && (
        <Card>
          <CardHeader><CardTitle className="text-base">Supervisor narrative</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {ev.strengths && <Narrative title="Summary of Strengths" body={ev.strengths} />}
            {ev.areas_for_development && <Narrative title="Areas for Development" body={ev.areas_for_development} />}
            {ev.goals && <Narrative title="Goals" body={ev.goals} />}
            {ev.training_assigned && <Narrative title="Training Assigned" body={ev.training_assigned} />}
            {ev.supervisor_comments && <Narrative title="Supervisor Comments" body={ev.supervisor_comments} />}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ratings detail ({rated.length} item{rated.length === 1 ? '' : 's'} rated)</CardTitle>
          <CardDescription>
            <span className="font-semibold">Rating scale:</span> 5 = Outstanding · 4 = Above Expectations · 3 = Meets Expectations · 2 = Needs Improvement · 1 = Unsatisfactory · N/A = Not Applicable
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            // Build a lookup of item_id -> { label, section_label } from the embedded template
            // so we can show the human-readable category for each rating instead of the raw code.
            const sections = ev.template?.sections || [];
            const itemMeta = {};
            sections.forEach((sec) => {
              const secLabel = sec.title || sec.label || sec.id || '';
              (sec.items || []).forEach((it) => {
                itemMeta[it.id] = { label: it.text || it.label || it.id, section: secLabel };
              });
            });
            // Group rated items by section in template order, falling back to a single
            // bucket for any item whose template entry isn't present (e.g. older evals).
            const sectionBuckets = sections.map((sec) => ({
              key: sec.id || sec.title,
              label: `Section ${sec.id}: ${sec.title || sec.label || ''}`.trim(),
              rows: (sec.items || [])
                .map((it) => rated.find((r) => r.item_id === it.id))
                .filter(Boolean)
                .map((r) => ({ id: r.item_id, label: itemMeta[r.item_id]?.label || r.item_id, na: r.na, rating: r.rating, comments: r.comments, core_value_example: r.core_value_example })),
            })).filter((b) => b.rows.length > 0);
            const orphanRows = rated
              .filter((r) => !itemMeta[r.item_id])
              .map((r) => ({ id: r.item_id, label: r.item_id, na: r.na, rating: r.rating, comments: r.comments, core_value_example: r.core_value_example }));
            if (orphanRows.length) sectionBuckets.push({ key: '__other__', label: 'Other', rows: orphanRows });

            if (sectionBuckets.length === 0) {
              return <p className="text-sm text-slate-500">No items rated.</p>;
            }
            return (
              <div className="space-y-4 max-h-[28rem] overflow-auto pr-1">
                {sectionBuckets.map((bucket) => (
                  <div key={bucket.key}>
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{bucket.label}</p>
                    <ul className="text-sm space-y-1">
                      {bucket.rows.map((r) => (
                        <li key={r.id} className="border-b py-1.5" data-testid={`public-rating-${r.id}`}>
                          <div className="flex justify-between items-start gap-3">
                            <span className="text-slate-800 leading-snug">{r.label}</span>
                            <span className="font-mono font-semibold whitespace-nowrap shrink-0">{r.na ? 'N/A' : r.rating}</span>
                          </div>
                          {r.comments && (
                            <div
                              className="mt-1.5 ml-2 pl-3 border-l-2 border-slate-300 text-slate-700 text-[13px] leading-snug whitespace-pre-wrap"
                              data-testid={`public-rating-comment-${r.id}`}
                            >
                              <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Manager note</span>
                              <p>{r.comments}</p>
                            </div>
                          )}
                          {r.core_value_example && (
                            <div
                              className="mt-1.5 ml-2 pl-3 border-l-2 border-emerald-300 text-slate-700 text-[13px] leading-snug whitespace-pre-wrap"
                              data-testid={`public-rating-core-value-${r.id}`}
                            >
                              <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Example shown</span>
                              <p>{r.core_value_example}</p>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {ev.sign_phase === 'part2' && ev.part2 && <Part2ReviewBlock p2={ev.part2} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your comments (optional)</CardTitle>
          <CardDescription>This is your chance to add comments before signing. Only you can edit this field.</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea rows={4} value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Add any feedback you'd like to record..." data-testid="public-employee-comments" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your signature</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <SignaturePad label={`${snap.first_name || ''} ${snap.last_name || ''}`} onSignatureChange={setSignature} testId="public-employee-signature" />
          <SignatureNotice hrEmail={ev.hr_email} />
          <Button onClick={onSubmit} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="public-sign-submit">
            <CheckCircle className="w-4 h-4 mr-2" />
            {busy ? 'Signing...' : 'Sign and submit'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-slate-500 text-xs">{label}</p>
      <p className="font-medium">{value || '—'}</p>
    </div>
  );
}

function Narrative({ title, body }) {
  return (
    <div>
      <p className="text-slate-500 text-xs uppercase tracking-wide">{title}</p>
      <p className="whitespace-pre-wrap">{body}</p>
    </div>
  );
}

const P2_SECTION_LABELS = {
  A: 'Professionalism', B: 'Safety & Security', C: 'Customer Service',
  D: 'Uniform & Appearance', E: 'Understanding of Requirements', F: 'Core Values Demonstration',
};

function Part2ReviewBlock({ p2 }) {
  const stillMet = p2.standards_still_met;
  const notMet = (p2.sections_not_met || []).map((l) => `${l} — ${P2_SECTION_LABELS[l] || l}`);
  return (
    <Card className="border-blue-200" data-testid="public-part2-block">
      <CardHeader className="bg-blue-50/60">
        <CardTitle className="text-base">6-Month Review</CardTitle>
        <CardDescription>Please read the six-month check-in below. This is what you are signing.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm pt-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="6-Month Due Date" value={p2.six_month_due_date} />
          <Field label="Reviewing Supervisor" value={p2.reviewing_supervisor_name} />
          {p2.site_title_if_changed && <Field label="Site / Title (if changed)" value={p2.site_title_if_changed} />}
          <Field label="Standards Confirmation" value={stillMet === true ? 'Yes' : stillMet === false ? 'No' : '—'} />
        </div>
        {stillMet === false && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3" data-testid="public-part2-notmet">
            <p className="text-red-900 text-xs uppercase tracking-wide font-semibold mb-1">Sections No Longer Met</p>
            <p className="text-red-900">{notMet.length ? notMet.join(', ') : '—'}</p>
          </div>
        )}
        {p2.progress_since_90_day && <Narrative title="Progress Since the 90-Day Evaluation" body={p2.progress_since_90_day} />}
        {p2.areas_still_developing && <Narrative title="Areas Still Requiring Development" body={p2.areas_still_developing} />}
        {p2.supervisor_comments && <Narrative title="Supervisor Comments" body={p2.supervisor_comments} />}
        {stillMet === false && p2.action_taken && <Narrative title="Action Taken" body={p2.action_taken} />}
      </CardContent>
    </Card>
  );
}

function PublicCredentialUpload({ token, scopedToken, credentials, optional }) {
  const mkForm = (it) => {
    const e = it.entered || {};
    return {
      license_type: e.license_type || (it.allowed_types && it.allowed_types.length === 1 ? it.allowed_types[0] : '') || '',
      state: e.state || it.state || '',
      license_number: '',
      expiration_date: e.expiration_date || '',
      qualification_date: e.qualification_date || '',
      range_or_instructor: e.range_or_instructor || '',
      result: e.result || '',
      score: e.score || '',
      label: 'front',
    };
  };
  const withRk = (list) => (list || []).map((it, idx) => ({ ...it, _rk: it.credential_id || `${it.item_key}::${idx}` }));
  const [items, setItems] = useState(() => withRk(credentials.items));
  const [forms, setForms] = useState(() => {
    const m = {};
    withRk(credentials.items).forEach((it) => { m[it._rk] = mkForm(it); });
    return m;
  });
  const [busyKey, setBusyKey] = useState('');
  const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const fmtMDY = (iso) => { const p = (iso || '').split('-'); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : iso; };

  const setField = (key, field, value) =>
    setForms((f) => ({ ...f, [key]: { ...f[key], [field]: value } }));

  const markReceived = (key) =>
    setItems((prev) => prev.map((i) => i._rk === key ? { ...i, status: 'received' } : i));

  const addState = (item) => {
    const nk = `${item.item_key}::new-${Date.now()}`;
    const clone = { ...item, _rk: nk, credential_id: null, state: '', status: 'requested', files: [], has_file: false, entered: null, reject_reason: null };
    setItems((prev) => [...prev, clone]);
    setForms((f) => ({ ...f, [nk]: mkForm(clone) }));
  };

  const licenseValid = (key) => {
    const f = forms[key] || {};
    return !!(f.license_type && (f.state || '').trim().length === 2 &&
      (f.license_number || '').trim() && f.expiration_date);
  };
  const licenseExpired = (key) => {
    const f = forms[key] || {};
    return f.expiration_date && f.expiration_date < todayISO();
  };
  const qualValid = (key) => {
    const f = forms[key] || {};
    return !!(f.qualification_date && f.qualification_date <= todayISO() &&
      (f.range_or_instructor || '').trim() && (f.result === 'Pass' || f.result === 'Fail') &&
      (!(f.score || '').trim() || (f.score || '').length <= 20));
  };

  const appendEntered = (form, key, kind) => {
    const f = forms[key] || {};
    if (kind === 'license') {
      form.append('license_type', f.license_type || '');
      form.append('state', f.state || '');
      form.append('license_number', f.license_number || '');
      form.append('expiration_date', f.expiration_date || '');
    } else if (kind === 'firearms') {
      form.append('qualification_date', f.qualification_date || '');
      form.append('range_or_instructor', f.range_or_instructor || '');
      form.append('result', f.result || '');
      form.append('score', f.score || '');
      form.append('state', f.state || '');
    }
  };

  const upload = async (item, file) => {
    const rk = item._rk;
    setBusyKey(rk);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('label', (forms[rk] || {}).label || 'front');
      appendEntered(form, rk, item.kind);
      await publicEvaluationAPI.uploadCredential(token, scopedToken, item.item_key, form);
      markReceived(rk);
      toast.success(`${item.item_label} uploaded`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
      return false;
    } finally { setBusyKey(''); }
  };

  const enterNoCard = async (item) => {
    const rk = item._rk;
    if (!qualValid(rk)) return;
    setBusyKey(rk);
    try {
      const f = forms[rk];
      await publicEvaluationAPI.enterQualification(token, scopedToken, item.item_key, {
        qualification_date: f.qualification_date, range_or_instructor: f.range_or_instructor,
        result: f.result, score: f.score, state: f.state || '',
      });
      markReceived(rk);
      toast.success('Qualification recorded — thank you');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save your qualification');
    } finally { setBusyKey(''); }
  };

  const badge = (s) => s === 'received' ? 'text-emerald-700' : s === 'verified' ? 'text-emerald-700' : s === 'rejected' ? 'text-red-700' : 'text-slate-500';

  const onFile = (it) => it.has_file || it.status === 'received' || it.status === 'verified';
  const expiredItems = items.filter((it) => it.kind === 'license' && it.entered?.expiration_date && it.entered.expiration_date < todayISO());
  const expiredKeys = new Set(expiredItems.map((it) => it._rk));
  const missingItems = items.filter((it) => !onFile(it) && !expiredKeys.has(it._rk));
  const autoOpen = !!optional && (missingItems.length > 0 || expiredItems.length > 0);
  const [open, setOpen] = useState(autoOpen);

  const renderItem = (item) => {
    const rk = item._rk;
    const f = forms[rk] || {};
    const detailsValid = item.kind === 'license' ? licenseValid(rk) : item.kind === 'firearms' ? qualValid(rk) : true;
    const chooseLabel = item.kind === 'firearms'
      ? (item.has_file ? 'Replace card photo' : 'Choose card photo')
      : (item.status === 'received' || item.status === 'verified' ? 'Add / replace file' : 'Choose file');
    const extra = (
      <>
        {item.kind === 'firearms' && (
          <Button type="button" size="sm" variant="ghost" disabled={busyKey === rk || !qualValid(rk)}
                  onClick={() => enterNoCard(item)} data-testid={`public-cred-nocard-${rk}`}>
            Enter without card
          </Button>
        )}
        {item.allows_multiple_states && (
          <Button type="button" size="sm" variant="ghost" onClick={() => addState(item)}
                  data-testid={`public-cred-addstate-${rk}`}>
            + Add another state
          </Button>
        )}
      </>
    );
    return (
      <div key={rk} className="border-b pb-3 last:border-0 space-y-2" data-testid={`public-cred-${rk}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-medium text-slate-800">{item.item_label}</div>
            <span className="text-xs rounded-full bg-indigo-100 text-indigo-800 px-2 py-0.5" data-testid={`public-cred-badge-${rk}`}>
              {item.credential_type || item.item_label}{f.state ? ` · ${f.state}` : (item.state ? ` · ${item.state}` : '')}
            </span>
          </div>
          <div className={`text-xs ${badge(item.status)}`} data-testid={`public-cred-status-${rk}`}>
            {item.status === 'received' ? 'Received — thank you'
              : item.status === 'verified' ? 'Verified'
              : item.status === 'rejected' ? 'Rejected — please re-upload'
              : 'Not yet provided'}
          </div>
        </div>
        {item.status === 'rejected' && item.reject_reason && (
          <div className="text-xs text-red-600" data-testid={`public-cred-reason-${rk}`}>
            Reason: {item.reject_reason}
          </div>
        )}

        {Array.isArray(item.files) && item.files.length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs text-slate-600" data-testid={`public-cred-files-${rk}`}>
            {item.files.map((fl) => (
              <span key={fl.file_id} className="rounded bg-slate-100 px-2 py-0.5">
                {fl.label ? `${fl.label}: ` : ''}{fl.display_name}
              </span>
            ))}
          </div>
        )}

        {item.kind === 'firearms' && item.texas_note && (
          <p className={`text-xs rounded px-2 py-1 ${item.texas_prominent ? 'bg-amber-100 text-amber-900 font-medium border border-amber-300' : 'text-slate-600 bg-slate-50'}`}
             data-testid={`public-cred-texas-${rk}`}>
            {item.texas_note}
          </p>
        )}

        {credentials.available && item.kind === 'license' && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {item.allowed_types && item.allowed_types.length > 1 ? (
                <select className="h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm text-slate-900"
                        value={f.license_type} onChange={(e) => setField(rk, 'license_type', e.target.value)}
                        data-testid={`public-cred-type-${rk}`}>
                  <option value="">Select type</option>
                  {item.allowed_types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              ) : (
                <Input value={f.license_type} placeholder="Type"
                       onChange={(e) => setField(rk, 'license_type', e.target.value)}
                       data-testid={`public-cred-type-${rk}`} />
              )}
              <Input value={f.state} maxLength={2} placeholder="State (e.g. TX)"
                     onChange={(e) => setField(rk, 'state', e.target.value.toUpperCase())}
                     data-testid={`public-cred-state-${rk}`} />
              <Input value={f.license_number} placeholder="License number"
                     onChange={(e) => setField(rk, 'license_number', e.target.value)}
                     data-testid={`public-cred-number-${rk}`} />
            </div>
            <LabeledDate id={`public-cred-exp-input-${rk}`} label={`${item.item_label} expiration date`}
                         value={f.expiration_date}
                         onChange={(e) => setField(rk, 'expiration_date', e.target.value)}
                         testid={`public-cred-exp-${rk}`} />
            {licenseExpired(rk) && (
              <p className="text-xs text-red-600" data-testid={`public-cred-expired-${rk}`}>
                This credential is expired. Please upload a current one.
              </p>
            )}
          </div>
        )}

        {credentials.available && item.kind === 'firearms' && (
          <div className="space-y-2">
            <LabeledDate id={`public-cred-qdate-input-${rk}`} label={`${item.item_label} qualification date`}
                         value={f.qualification_date} max={todayISO()}
                         onChange={(e) => setField(rk, 'qualification_date', e.target.value)}
                         testid={`public-cred-qdate-${rk}`} />
            <div className="grid grid-cols-2 gap-2">
              <Input value={f.state} maxLength={2} placeholder="State (e.g. TX)"
                     onChange={(e) => setField(rk, 'state', e.target.value.toUpperCase())}
                     data-testid={`public-cred-fstate-${rk}`} />
              <Input value={f.range_or_instructor} placeholder="Range or instructor"
                     onChange={(e) => setField(rk, 'range_or_instructor', e.target.value)}
                     data-testid={`public-cred-range-${rk}`} />
              <select className="h-9 w-full rounded border border-slate-300 bg-white px-2 text-sm text-slate-900"
                      value={f.result} onChange={(e) => setField(rk, 'result', e.target.value)}
                      data-testid={`public-cred-result-${rk}`}>
                <option value="">Pass or Fail</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
              </select>
              <Input value={f.score} maxLength={20} placeholder="Score (optional)"
                     onChange={(e) => setField(rk, 'score', e.target.value)}
                     data-testid={`public-cred-score-${rk}`} />
            </div>
          </div>
        )}

        {credentials.available && (
          <StagedUpload testid={`public-credup-${rk}`} accept=".jpg,.jpeg,.png,.heic,.pdf"
                        label={f.label} onLabelChange={(v) => setField(rk, 'label', v)}
                        detailsValid={detailsValid} busy={busyKey === rk}
                        onUpload={(file) => upload(item, file)} chooseLabel={chooseLabel} extra={extra} />
        )}
      </div>
    );
  };

  const body = items.map(renderItem);

  if (optional) {
    return (
      <Card data-testid="public-credentials-optional">
        <CardHeader className="cursor-pointer" onClick={() => setOpen((o) => !o)} data-testid="optional-cred-toggle">
          <CardTitle className="text-base flex items-center justify-between gap-2">
            <span>Add or update credentials (optional)</span>
            <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </CardTitle>
          <CardDescription>
            Renewed a license or have a new credential? You can add it here. This is optional and will not delay signing.
          </CardDescription>
        </CardHeader>
        {open && (
          <CardContent className="space-y-4">
            {(missingItems.length > 0 || expiredItems.length > 0) && (
              <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 space-y-1" data-testid="optional-cred-summary">
                {missingItems.map((it) => (
                  <div key={`m-${it._rk}`} data-testid={`optional-cred-missing-${it._rk}`}>{it.item_label} — Not on file yet</div>
                ))}
                {expiredItems.map((it) => (
                  <div key={`e-${it._rk}`} data-testid={`optional-cred-expired-${it._rk}`}>{it.item_label} — Expired {fmtMDY(it.entered.expiration_date)}</div>
                ))}
              </div>
            )}
            {body}
          </CardContent>
        )}
      </Card>
    );
  }

  return (
    <Card data-testid="public-credentials">
      <CardHeader>
        <CardTitle className="text-base">Required documents</CardTitle>
        <CardDescription>
          {credentials.available
            ? 'Please provide the details and a clear photo or PDF of each document before you sign. You can take a photo or choose a saved file.'
            : 'Document uploads are temporarily unavailable. Please contact your supervisor.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {body}
      </CardContent>
    </Card>
  );
}
